import { ObjectHandleRepository } from 'util/util';
import { IDictionary } from 'util/domain';
import { CommonUtilsService } from 'util/services/commonUtilsService'
import { ProgressService } from 'phoenix/services';

/**
 * Threshold of time (in milliseconds) to wait for loading images
 */
const IMAGE_TIMEOUT_PERIOD = 30000;
/**
 *  Minimum width in pixels to request a page image.
 */
const MIN_PAGE_WIDTH = 800;
/**
 * Percentage interval to zoom in and out by.
 */
const ZOOM_INTERVAL = 1.30;
const MIN_ZOOM_LEVEL = 0.40;
const MAX_ZOOM_LEVEL = 10;
/**
 * Width threshold in pixels with which the next page image should be requested on zoom.
 */ 
const PAGE_WIDTH_INTERVAL = 200;
const MAX_WIDTH_HEADER = 'content-max-width';
const CONTENT_WIDTH_HEADER = 'content-width';
const DEBOUNCE_DELAY = 100;
const PAGE_CLASS = 'sb-dvwindow__page';
const SEPARATOR_CLASS = 'sb-dvwindow__pagesep';

interface MozDocument extends Document {
	mozFullScreenElement:HTMLElement;
	mozFullScreenEnabled:boolean;
	mozCancelFullScreen:() => void;
}

interface MsDocument extends Document {
	msFullscreenElement:HTMLElement;
	msFullscreenEnabled:boolean;
	msExitFullscreen:() => void;
}

interface ViewerClient {
	mode:DocViewerMode;
	docFormat:DocViewerFormat;
	enableTextSelection:() => ng.IPromise<boolean>;
	pageCount:number;
	/**
	 * The URI to populate an image tag with. This can either be a real URI or a data URI.
	 */
	getPageUri:(page:number, tabIndex:number, maxWidth?:number) => ng.IPromise<string>;
	getMediaInfo:() => ng.IPromise<any>;
	getPageContent?:(page:number, tab:number) => ng.IPromise<{img:string, format:string}>;
}

function isMozDocument(doc:Document|MozDocument|MsDocument):doc is MozDocument {
	return (<MozDocument>doc).mozFullScreenElement !== undefined;
}

function isMsDocument(doc:Document|MozDocument|MsDocument):doc is MsDocument {
	return (<MsDocument>doc).msFullscreenElement !== undefined;
}
export enum DocViewerFormat {
	SVG = 'svg',
	IMG = 'img',
	TXT = 'txt'
}

export enum DocViewerMode {
	MULTIPAGE = 'MultiPage',
	TABBED = 'TabbedMultiPage',
	SLIDE = 'Slide',
	PICTURE = 'Picture'
}
enum ScrollDirection {
	Down = 'd',
	Up = 'u'
}

interface DocumentTab {
	index:number;
	selected:boolean;
	name:string;
}

interface DocViewerScope extends ng.IScope{
	vm:DocViewerController;
}

interface PageMetadata {
	page:number;
	loaded:boolean;
	loading:boolean;
	width:number;
	nextWidth?:number;
	percView?:number;	
}

interface PageElemMetadata {
	pageNumber:number;
	naturalWidth?:number;
	naturalHeight?:number;
}

require('./documentViewer.styles');
let template = require('./documentViewer.template');

export function DocViewerDirective(
	$timeout:ng.ITimeoutService,
	$window:ng.IWindowService
) {
	return {
		restrict: 'E',
		controller: DocViewerController,
		controllerAs: 'vm',
		bindToController: {
			config: '=viewerSettings',
			isLoading: '=',
			showSmallToolbar: '=',
			documentMimeType: '='
		},
		template,
		scope: {},
		link: (scope:DocViewerScope, element, attrs) => {
			scope.vm.needTabScrollBtns = false;

			let resize = scope.vm.viewerResize.bind(scope.vm);

			angular.element($window).on('resize', resize);

			let changePage = function(e){
				if (e.keyCode === 39){
					scope.vm.goToNextPage();
				} else if (e.keyCode === 37){
					scope.vm.goToPreviousPage();
				}				
			}

			scope.$on('$destroy', () => {
				angular.element($window).off('resize', resize);
				if (scope.vm.mode === DocViewerMode.SLIDE){
					angular.element(document).off('keyup', changePage);
				}

				scope.vm.destroy();
			});

			let testForTabScroll = () => {
				let el = angular.element($window.document.getElementById('sb-dvtoolbar__tabframe'));
				if (el.length !== 0) {
					scope.vm.needTabScrollBtns = (el[0].clientWidth < el[0].scrollWidth);
				}
			}

			scope.$watch(
				() => scope.vm.tabList ? scope.vm.tabList.length : undefined,
				(newVal, oldVal) => {
					if (newVal !== oldVal) {
						$timeout(() => testForTabScroll(), 50, true);
					}
				}
			);

			if (scope.vm.isTabView) {
				scope.$watch(
					() => scope.vm.viewport.getBoundingClientRect().width,
					(newVal, oldVal)  => {
						if (newVal !== oldVal) {
							testForTabScroll();
						}
					}
				);
			}
			else {
				angular.element($window.document.getElementById('sb-dvtoolbar')).addClass('sb-dvtoolbar-center');
			}
			
			scope.$watch('vm.isPresenting', (newVal, oldVal) => {
				if (newVal === undefined) {
					return;
				}

				if (newVal && (
					$window.document.fullscreenEnabled || 
					$window.document.webkitFullscreenEnabled || 
					(isMozDocument($window.document) && $window.document.mozFullScreenEnabled) ||
					(isMsDocument($window.document) && $window.document.msFullscreenEnabled))
				) {
					if (element[0].requestFullscreen) {
						element[0].requestFullscreen();
					}				
					else if (element[0].webkitRequestFullscreen) {							
						element[0].webkitRequestFullscreen();
					}
					else if (element[0].mozRequestFullScreen) {							
						element[0].mozRequestFullScreen();
					}						
					else if (element[0].msRequestFullscreen) {							
						element[0].msRequestFullscreen();
					}						
				}
				else {
					//close full screen
					if (
						$window.document.fullscreenElement || 
						$window.document.webkitFullscreenElement || 
						(isMozDocument($window.document) && $window.document.mozFullScreenElement && $window.document.mozCancelFullScreen) ||
						(isMsDocument($window.document) && $window.document.msFullscreenElement && $window.document.msExitFullscreen)
					) {
						if ($window.document.exitFullscreen) {
							$window.document.exitFullscreen();
						}				
						else if ($window.document.webkitExitFullscreen) {
							$window.document.webkitExitFullscreen();
						}
						else if (isMozDocument($window.document) && $window.document.mozCancelFullScreen) {							
							$window.document.mozCancelFullScreen();
						}						
						else if (isMsDocument($window.document) && $window.document.msExitFullscreen) {							
							$window.document.msExitFullscreen();
						}			
					}
				}

				scope.vm.viewerResize();
			});

			//watch for change in full screen element
			scope.$watch(() => { 
				return (
					$window.document.fullscreenElement || 
					$window.document.webkitFullscreenElement || 
					(isMozDocument($window.document) && $window.document.mozFullScreenElement) ||
					(isMsDocument($window.document) && $window.document.msFullscreenElement)
				);
			}, (newVal, oldVal) => {
				//if null and presenting then we are closing full screen
				if (!newVal && scope.vm.isPresenting) {
					scope.vm.isPresenting = false;
					scope.vm.viewerResize();
				}
			});

			//show/hide bottom mobile bar on scroll
			let scroller = element[0].querySelector('#sb-dvwindow');

			scope.$watch(
				() => scroller.scrollTop,
				//if the document is scrolled near the top show the toolbar
				(newVal, oldVal) => scope.vm.showSmallToolbar = !newVal || newVal < 60
			);
			
			scope.$watch('vm.activeTab', (newVal, oldVal) => scope.vm.setCurrentTab(newVal));
			scope.$watch('vm.config', (newVal, oldVal) => {
				if (!newVal) {
					return;
				}					
				scope.vm.init();

				if (scope.vm.mode === DocViewerMode.SLIDE){
					angular.element(document).on('keyup', changePage);
				}
			});
		}
	};
}

DocViewerDirective.$inject = [
	'$timeout',
	'$window'
];
export class DocViewerController {
	public static $inject = [
		'$window',
		'$http',
		'$interval',
		'$q',
		'$timeout',
		'utils',
		'objectHandleRepository',
		'pxEnvironmentService',
		'pxProgressService'
	];

	constructor(
		private $window:ng.IWindowService,
		private $http:ng.IHttpService,
		private $interval:ng.IIntervalService,
		private $q:ng.IQService,
		private $timeout:ng.ITimeoutService,
		private utils:CommonUtilsService,
		private objectHandleRepo:ObjectHandleRepository,
		private pxEnvironmentService,
		private pxProgressService:ProgressService
	) {
	}
	
	private firstLoad:boolean = true;
	private isMultiPage:boolean;
	private isPictureView:boolean;
	private isSlideView:boolean;
	private isLoading:boolean;
	private pageCount:number;
	private canSelectText:boolean;
	public viewport:HTMLElement;
	private contentDiv:JQuery;
	private pageScale:number;
	private svgScale:number;
	private narrowMargin = 8;
	private pageMargin = 16;
	private contentWidth:number = 0;
	private pageWidth:number = 0;
	/**
	 * The maximum width in pixels which the document can be requested to for increased quality.
	 */
	private pageMaxWidth:number;
	private scrollbarWidth:number;
	private defaultHeight:number;
	private zoomLevel = 1;
	private userChangedZoom = false;
	private currentPage = 1;
	private currentTab = 0;
	private loadedBytes= 0;
	private evictThresholdSize  = this.isMobileDevice ? 512000 : 5000000;
	private evictThresholdCount  = this.isMobileDevice ? 7 : 25;
	private lastScrollTop = 0;
	private scrollDir:ScrollDirection = ScrollDirection.Down;
	private scrollTrig = false;
	private scrollLength = 0.5;
	private scrollDelay = 400;
	private scrollDebounce:boolean;	
	private setResizePromise:ng.IPromise<void>;
	private config:(controller:DocViewerController)=>ViewerClient;
	private client:ViewerClient;
	public mode:DocViewerMode;
	private docFormat:DocViewerFormat;
	private pageQueue:number[] = [];
	private pageStatuses:IDictionary<PageMetadata> = {};
	private pageMetadata = new WeakMap<Element, PageElemMetadata>();
	
	/**
	*  Very simple mobile detection. Not 100% reliable.
	*/
	private get isMobileDevice() {
		return (typeof window.orientation !== 'undefined') || (navigator.userAgent.indexOf('IEMobile') !== -1);
	} 

	/**
	* Used to blur page input after submitting page number
	*/
	public inputDisabled = false; 
	public inputPage:string;
	public isTabView:boolean;
	public isPresenting:boolean;
	public showSmallToolbar:boolean;
	public tabList:DocumentTab[] = [];
	public needTabScrollBtns:boolean;

	private parseScale(text:string) {
		let scale = +text.substring(6).split(/[^0-9.]/)[0];
		if (!scale) {
			scale = 1;
		}
		return scale;
	}

	private getSVGScale(svgElem, level) {
		for (let i = 0; i < svgElem.childNodes.length; ++i) {
			let el:Node & HTMLElement= svgElem.childNodes[i];
			if (el.tagName === 'g') {
				let xform = el.getAttribute('transform');
				if (xform && xform.indexOf('scale(') === 0) {
					this.svgScale = this.parseScale(xform);
					return true;
				}
				if (level < 2 && this.getSVGScale(el, level + 1)) {
					return true;
				}
			}
		}
		return false;
	}

	private parseNumericAttribute(attrVal:string) {
		// https://regex101.com/r/0xJwGK/1/
		const unitValueRegex = /^(\d+)(pt|px|pc|em)$/gm;
		let matches = unitValueRegex.exec(attrVal);
		if (matches && matches.length > 0) {
			return +matches[1];
		}

		return +attrVal;
	}

	private disableLink(ev:Event) {
		ev.preventDefault();
	}

	private processSVGDOM(svgElem:HTMLElement) {
		this.getSVGScale(svgElem, 0);
		let atags:NodeList = svgElem.getElementsByTagName('a');
		for (let i = 0; i < atags.length; ++i) {
			let aEl = (atags.item(i) as HTMLElement);
			let href = aEl.getAttribute('href');
			aEl.removeAttribute('href');
			aEl.setAttribute('target', '_blank'); //make all links open in new tab
			if (href) {
				if (href.charAt(0) !== '#') {
					aEl.setAttribute('target', '_blank');
					aEl.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', href);
				}
				else {
					aEl.onclick = e => this.disableLink(e);
				}
			}
		}
	}

	private setPageScale() {
		if (this.isSlideView) {
			this.pageMargin = this.narrowMargin;
		}
		let xScale = (this.viewport.offsetWidth - this.scrollbarWidth - (this.pageMargin * 2)) / this.pageWidth;
		if (this.isSlideView || this.isPictureView) {
			let yScale = (this.viewport.clientHeight - (this.narrowMargin * 2)) / this.defaultHeight;
			xScale = Math.min(xScale, yScale);
		}
		if (this.isPictureView && xScale > 1) {
			xScale = 1;
		}
		else if (this.isTabView && xScale < 0.9) {
			xScale = 0.9;
		}
		this.pageScale = xScale;
		this.contentWidth = this.calculateContentWidth(this.pageWidth, this.pageScale, this.pageMargin, this.zoomLevel);
	}

	private calculateContentWidth(pageWidth:number, pageScale:number, pageMargin:number, zoomLevel:number) {
		return (pageWidth * pageScale * zoomLevel) + (pageMargin * 2);
	}

	/**
	 * Reports the actual width of the element representing a page.
	 */
	private getRealContentWidth() {
		let viewportWidth = this.viewport.clientWidth - this.pageMargin * 2;
		let windowContent = this.viewport.querySelectorAll('.sb-dvwindow__content')[0];
		let windowContentWidth = windowContent && windowContent.clientWidth > 1 ? windowContent.clientWidth : null;
		return windowContentWidth ? windowContentWidth : viewportWidth;
	}

	private processPageSVG(xmlImg:string, page:number) {
		const iSvgStart = xmlImg.indexOf('<svg');
		if (iSvgStart <= 0) {
			return;
		}

		if (this.isTabView && page === 1) {
			// for spreadsheets, the server returns the number of 'pages' (i.e. sections)
			// on the worksheet in a comment at the beginning of the svg content response
			let iSecStart = xmlImg.indexOf('<!-- sections:');
			iSecStart += 14;
			let iSecEnd = xmlImg.indexOf(' ', iSecStart) + 1;
			this.pageCount = +(xmlImg.substring(iSecStart, iSecEnd));
		}
		else if (!this.isMultiPage) {
			page = 1;
		}

		let iSvgEnd = xmlImg.indexOf('>', iSvgStart) + 1;
		let svgTag = xmlImg.substring(iSvgStart, iSvgEnd);
		let svgEl = angular.element(svgTag)[0];
		let docWidth:number;
		let docHeight:number;
		let vb = svgEl.getAttribute('viewBox');

		if (vb) {
			let vbAttrs = vb.split(' ');
			docWidth = +vbAttrs[2];
			docHeight = +vbAttrs[3];
		}
		else {
			docWidth = this.parseNumericAttribute(svgEl.getAttribute('width'));
			docHeight = this.parseNumericAttribute(svgEl.getAttribute('height'));
		}

		if (page === 1) {
			this.contentDiv.empty();
			this.pageWidth = docWidth;
			this.defaultHeight = docHeight;
			this.setPageScale();

			const firstPage = this.createPageElem(1);
			const firstSep = this.createSeparatorElem(1);
			this.pageMetadata.set(firstPage, {
				pageNumber: 1
			});

			this.contentDiv
				.css('width', `${this.contentWidth - (this.pageMargin * 2)}px`)
				.css('padding-left', `${this.pageMargin}px`)
				.css('padding-right', `${this.pageMargin}px`)
				.append(firstSep)		
				.append(firstPage);
				
			if (this.isMultiPage) {
				angular.element(this.viewport).on('scroll', e => this.viewerScroll(e));
			}
		}

		const pageEl = this.$window.document.getElementById(`dv-pg${page}`)
		const svg = `<svg xmlns="http://www.w3.org/2000/svg xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 ${docWidth} ${docHeight}" width="100%" height="100%" preserveAspectRatio="none">`;		
		const meta = this.pageMetadata.get(pageEl);

		xmlImg = svg + xmlImg.substr(iSvgEnd);
		meta.naturalHeight = docHeight;
		meta.naturalWidth = docWidth;

		angular
			.element(pageEl)
			.css('height', `${docHeight * this.pageScale * this.zoomLevel}px`)
			.html(xmlImg);		

		this.client
			.enableTextSelection()
			.then(res => this.canSelectText = res);

		svgEl = (pageEl.childNodes[0] as HTMLElement);

		this.processSVGDOM(svgEl);
		this.exchangePage(page, xmlImg.length);
		this.centerDocument();
		this.populatePlaceholders(page);
	}

	/*
	* This function is here to process error pages.  The client requested
	* an SVG page but the server returned it as b64-encoded image, most likely
	* because an error ocurred.
	*/
	private processPageImage(imgText, page:number) {
		if (page === 1) {
			// if an error occurs on page 1, then abandon any
			// attempts at displaying subsequent pages
			this.pageCount = 1;

			this.contentDiv.empty();
			this.contentDiv
				.append(this.createSeparatorElem(1))
				.append(this.createPageElem(1));
		}

		let pageEl = angular.element(this.$window.document.getElementById(`dv-pg${page}`));
		pageEl.html(`<img id="dv-img${page}" src="" hidden>`);
		let imgEl = angular.element(this.$window.document.getElementById(`dv-img${page}`));
		imgEl.attr('src', imgText);

		let docWidth = (imgEl[0] as HTMLImageElement).naturalWidth;

		if (page === 1) {
			let scale = 1;
			let frameW = this.viewport.clientWidth - 80;
			if (frameW < docWidth) {
				scale = frameW / docWidth;
			}

			this.pageWidth = docWidth;
			this.pageScale = scale;
			this.contentDiv.css('width', '' + (this.pageWidth * scale) + 'px');
		}
		imgEl.attr('width', '100%');
		imgEl.attr('height', '100%');
		imgEl.show();

		this.exchangePage(page, 10000);
	}

	private scalePageToWidth(naturalHeight:number, naturalWidth:number, targetWidth:number, defaultScale?:number) {
		let scale = 1;
		if (defaultScale) {
			scale = defaultScale;
		}

		scale = targetWidth/naturalWidth;
		return naturalHeight * scale;
	}

	private loadPageImage(url:string, page:number) {
		if (this.firstLoad && page === 1) {
			const firstPage = this.createPageElem(1);
			const firstSep = this.createSeparatorElem(1);
			this.pageMetadata.set(firstPage, {
				pageNumber: 1
			});

			this.contentDiv.empty();
			this.contentDiv
				.append(firstSep)
				.append(firstPage);
		}
		
		const imgElemId = `dv-img${page}`;
		const unregister = () => {
			angular.element(imgEl).off('load', onLoad);
		};
		let deferred = this.$q.defer();
		let timeout = this.$timeout(() => {
			unregister();
			deferred.reject(new Error('Failed to load image in a timely manner.'));
		}, IMAGE_TIMEOUT_PERIOD);
		
		let pageEl = this.$window.document.getElementById(`dv-pg${page}`);
		let imgEl = this.$window.document.getElementById(imgElemId) as HTMLImageElement;
		if(!imgEl){
			imgEl = this.$window.document.createElement('img');
			angular
				.element(imgEl)
				.attr('id', imgElemId)
				.attr('hidden', '');
			angular
				.element(pageEl)
				.empty()
				.append(imgEl);
		}		

		let onLoad = (ev => {
			const docWidth = imgEl.naturalWidth;
			const docHeight = imgEl.naturalHeight;
			if (this.firstLoad) {
				this.pageWidth = docWidth;
				this.defaultHeight = docHeight;
				this.setPageScale();

				this.contentDiv.css('width',` ${this.contentWidth - (this.pageMargin * 2)}px`);
				this.contentDiv.css('padding-left', `${this.pageMargin}px`);
				this.contentDiv.css('padding-right', `${this.pageMargin}px`);
				if (this.isMultiPage) {
					angular.element(this.viewport).on('scroll', e => this.viewerScroll(e));
				}
			}

			let newHeight = this.scalePageToWidth(docHeight, docWidth, this.contentWidth - (this.pageMargin * 2), this.pageScale);
			angular.element(pageEl).css('height', `${newHeight}px`)

			const meta = this.pageMetadata.get(pageEl);
			meta.naturalWidth = docWidth;
			meta.naturalHeight = docHeight;

			angular
				.element(imgEl)
				.removeAttr('hidden')
				.attr('width', '100%')
				.attr('height', 'auto')
				
			unregister();
			
			this.exchangePage(page);
			this.centerDocument();
			this.populatePlaceholders(page);
			this.$timeout.cancel(timeout);
			deferred.resolve(page);
		}).bind(this);

		angular
			.element(imgEl)
			.on('load', onLoad)
			.attr('src', url);

		return deferred.promise;
	}

	private populatePlaceholders(page:number) {
		if (page === 1 && this.isMultiPage && this.firstLoad) {			
			// append elements to fragment to reduce operation to 1 page reflow
			const frag = this.$window.document.createDocumentFragment();
			let page:Element;
			let separator:Element; 

			for (let pg = 2; pg <= this.pageCount; ++pg) {
				page  = this.createPageElem(pg, this.defaultHeight * this.pageScale);
				separator = this.createSeparatorElem(pg);

				frag.appendChild(separator);
				frag.appendChild(page);				

				this.pageMetadata.set(page, {
					pageNumber: pg,
					naturalWidth: this.pageWidth,
					naturalHeight: this.defaultHeight
				})
			}

			frag.appendChild(this.createSeparatorElem());
			this.contentDiv.append(frag);
		}
		else if (this.isPictureView) {
			this.contentDiv.append(this.createSeparatorElem());
		}
	}

	private loadTab(tabIndex) {
		this.currentPage = 1;
		this.currentTab = tabIndex;
		this.pageCount = 1;
		this.zoomLevel = 1;
		this.loadPage(1);
	}

	/**
	 * Plucks off and loads pages from the queue until it's empty.
	 */
	private loadQueue(){
		let promises:ng.IPromise<void>[] = [];
		/**
		* Pop the next page from the queue and load it. 
		*/
		const loadNextPage = () => {
			let toLoad = this.pageQueue.shift();
			if (!angular.isDefined(toLoad) || !this.shouldLoadPage(toLoad)) {
				return this.$q.resolve(); 
			}

			let page = this.pageStatuses[toLoad];
			let width = page ? page.nextWidth : this.getRealContentWidth();
			return this.loadPage(toLoad, this.clampWidth(width));	
		};
		
		while(this.pageQueue.length > 0){
			promises.push(loadNextPage());
		}

		return this.$q.all(promises);
	}

	private loadPage(pageNumber:number, requestedWidth:number = MIN_PAGE_WIDTH) {	
		let onContent:ng.IPromise<any> = this.$q.resolve();			
		if(!(pageNumber in this.pageStatuses)){
			this.pageStatuses[pageNumber] = {
				page: pageNumber,
				loaded: false,
				loading: false,
				width: 0
			};
		}
		let pageStatus = this.pageStatuses[pageNumber];

		// if it's an existing page and we're requesting a new width,
		// make sure it's greater than it was previously
		if(!this.firstLoad && this.docFormat === DocViewerFormat.IMG && pageStatus.width > 0 && pageStatus.width >= requestedWidth){
			return onContent;
		}

		pageStatus.loading = true;
	
		switch(this.docFormat) {
			case DocViewerFormat.IMG:		
				// the content is to be loaded by the browser via <img> tag
				onContent = this.client.getPageUri(pageNumber, this.currentTab, requestedWidth).then(res => {
					let uri:string = res;
					let metadata = this.objectHandleRepo.getMetadata(uri);		
					pageStatus.width = requestedWidth;

					if(metadata){
						if(this.firstLoad && MAX_WIDTH_HEADER in metadata){
							this.pageMaxWidth = +metadata[MAX_WIDTH_HEADER];
						}

						// reassign the width if the API differs from the requested value
						if(CONTENT_WIDTH_HEADER in metadata){
							pageStatus.width = +metadata[CONTENT_WIDTH_HEADER];
						}
					}
					
					return this.loadPageImage(uri, pageNumber);
				});
				break;
			case DocViewerFormat.SVG:
				let svgPromise:ng.IPromise<any>;	
				if (angular.isDefined(this.client.getPageContent)) {
					svgPromise = this.client.getPageContent(pageNumber, this.currentTab);
				}
				else {
					svgPromise = this.client
						.getPageUri(pageNumber, this.currentTab)
						.then(uri => this.loadSvg(uri));				
				}

				onContent = svgPromise
					.then(svg => {
						let xmlImg = svg.img;
						let contentType = svg.format;
						if (contentType === 'image/svg+xml') {
							this.processPageSVG(xmlImg, pageNumber);
						}
						else {
							this.processPageImage(xmlImg, pageNumber);
						}					
					});
				break;
		}


		return onContent.then(() => {			
			pageStatus.loaded = true;
		})
		.finally(() => {
			pageStatus.loading = false;
		});
	}

	/**
	 * Removes content from pages not in view when a new page is loaded, supporting
	 * virtualization by limiting the total number pages at any given time.
	 */
	private exchangePage(pageNumber:number, pageSize?:number) {
		let loadedPages = this.utils.dictToArray(this.pageStatuses).filter(p => p.loaded);
		let isSvg = this.docFormat === DocViewerFormat.SVG;

		if (isSvg) {
			this.loadedBytes += pageSize;
		}
		else {
			pageSize = 0;
		}
		
		if (pageNumber > 1) {
			// we test for the page count to disable this functionality 
			// for small documents and presentations
			let removePage = isSvg ?
				this.loadedBytes > this.evictThresholdSize && loadedPages.length > 4 :  // 5M
				loadedPages.length >= this.evictThresholdCount;

			if (removePage) {
				let distance = 0;

				// find the loaded page farthest away from the current and unload it
				const killPageNum = loadedPages.reduce((killPage, loaded) => {
					let d = Math.abs(loaded.page - pageNumber);
					// page 1 is never unloaded
					if (loaded.page > 1 && d > distance) {
						distance = d;
						return loaded;
					}
					else {
						return killPage;
					}
				}).page;
		
				angular.element(this.$window.document.getElementById(`dv-pg${killPageNum}`)).empty();

				this.pageStatuses[killPageNum] = null;
				delete this.pageStatuses[killPageNum];
				
				if (isSvg) {
					this.loadedBytes -= pageSize;
				}
			}
		}
	}

	private loadSvg(apiUri:string) {
		let config = { 
			headers: { 
				Accept: 'image/svg+xml,image/jpeg' 
			}
		};       

		return this.$http
			.get<string>(apiUri, config)
			.then(response => {
				return { 
					img: response.data,
					format: response.headers('Content-Type')
				};
			});
	}


	private viewerScroll(ev:Event) {
		let el = ev.currentTarget as HTMLElement;
		this.scrollDir = el.scrollTop > this.lastScrollTop ? ScrollDirection.Down : ScrollDirection.Up;
		this.lastScrollTop = el.scrollTop;
		if (!this.scrollTrig) {
			this.scrollTrig = true;
			let pageSize = this.viewport.scrollHeight / this.pageCount;
			this.$timeout(() => {
				this.scrollTrig = false;
				let scrollDist = Math.abs(this.lastScrollTop - this.viewport.scrollTop);
				let pgDist = scrollDist / pageSize;
				if (pgDist < this.scrollLength) {
					this.evaluateAndLoad();
				}

			}, this.scrollDelay, false);
		}
	}

	/**
	 * Convenience method for evaluating the view for pages that needed to be loaded
	 * and subsequently loading them.
	 */
	private evaluateAndLoad(){
		this.evaluateView();
		return this.loadQueue();
	}

	/**
	* This function, loadQueue(), and exchagePage() are the heart of the virtualization
	* system. 
	*	
	* Starting at the top of the viewer window, this function determines
	* the visible pages and adds them to a queue if they have not been loaded.  It
	* then determines the page before of after (depending on the scroll direction)
	* and adds that page as well if it has not been loaded.
	*/
	private evaluateView = (() => {

		let evaluating = false;

		// create closure with IIFE to enable function-scoped state
		return () => {
			if(evaluating){
				return;
			}

			evaluating = true;

			const pages = this.getPageElements();
			const vframeRect = this.viewport.getBoundingClientRect();
			const midPoint = vframeRect.height / 2;
			const topElem = this.getViewedPage();	
			const topPageNum = this.pageMetadata.get(topElem).pageNumber;
			
			let pgElem:HTMLElement = topElem;
			let pageNum:number = topPageNum;
			let pageIndex = pageNum - 1;
			let newPageNum:number;
			let adjPageElem:Element;
			let pgRect = pgElem.getBoundingClientRect();
			let pos = Math.min(pgRect.bottom, vframeRect.height);

			if (this.shouldLoadPage(pageNum)) {
				this.pageQueue.push(pageNum);
			}

			if (pos > midPoint) {
				newPageNum = pageNum;
			}

			
			while (pos < vframeRect.height) {				
				if (!pages[pageIndex + 1]) {
					break;
				}

				pageIndex++;
				pageNum++;
				pgElem = pages[pageIndex];
				pgRect = pgElem.getBoundingClientRect();

				if (this.shouldLoadPage(pageNum)) {
					this.pageQueue.push(pageNum);
				}
				
				// if the current page doesn't cross the midpoint and the next page does, 
				// consider it to be active
				if (pos <= midPoint && (pgRect.top + pgRect.height) > midPoint) {
					newPageNum = pageNum;
				}

				pos = pgRect.top + pgRect.height;
			}

			// now check the page above or below depending on the current direction of scroll
			if(pgElem){
				if (this.scrollDir === ScrollDirection.Down && newPageNum < this.pageCount) {
					adjPageElem = pages[pageIndex + 1];
					pageNum++;
				}
				else if(this.scrollDir === ScrollDirection.Up){
					adjPageElem = pages[pageIndex - 1];
					pageNum = Math.max(topPageNum - 1, 1);
				}
			}

			if (adjPageElem && this.shouldLoadPage(pageNum)) {
				this.pageQueue.push(pageNum);
			}

			// if the current page has changed, schedule a digest cycle
			if (newPageNum && newPageNum !== this.currentPage) {
				this.currentPage = newPageNum;
				this.$timeout(angular.noop, 1, true);
			}

			// sort the list so that the page(s) that are the most visible are loaded first
			this.pageQueue.sort((a, b) => {
				if(a === b){
					return 0;
				}
				else {
					return a > b ? 1 : -1;
				}
			});

			evaluating = false;
		};
	})();

	private shouldLoadPage(page:number) {
		return page > 0 && page <= this.pageCount && !this.isPageLoading(page) && !this.isPageLoaded(page)
	}

	/**
	* Finds the page at the top of the viewport.
	*/
	private getViewedPage() {
		const pages = this.getPageElements();
		const elem = CommonUtilsService.binarySearch(pages, page => {
			const rect = page.getBoundingClientRect();

			// an element is considered at the top of the viewport if its 
			// top value is between 0 (completey in view) or 
			// greater than its negative height (entirely obscured)
			if(rect.top <= 0 && rect.top > (rect.height * -1)){
				return 0;
			}
			else {
				return rect.top > 0 ? -1 : 1;
			}			
		}, true);

		return elem;
	}

	private getPageElements(){
		return CommonUtilsService.toArray(this.viewport.getElementsByClassName(PAGE_CLASS) as NodeListOf<HTMLElement>);
	}

	/*
	* Returns true if the img element for the given page is in the process of being loaded.
	*/
	private isPageLoading(page:number) {
		return page in this.pageStatuses && this.pageStatuses[page].loading;
	}

	/*
	* Returns true if the img element for the given page is loaded.
	*/
	private isPageLoaded(page:number){
		return page in this.pageStatuses && this.pageStatuses[page].loaded;
	}

	private scrollToPage(pageNumber:number, jump = false) {
		if (!this.scrollDebounce) {
			let scrollOptions:ScrollIntoViewOptions = {
				behavior: 'smooth',
				block: 'start', 
				inline: 'nearest'
			};

			if (jump && !this.pxEnvironmentService.isIE) {
				scrollOptions.behavior = 'auto';
			}

			this.$window.document.getElementById(`dv-pgsep${pageNumber}`)
				.scrollIntoView(scrollOptions);

			this.scrollDebounce = true;
			this.$timeout(() => this.scrollDebounce = false, 250);
		}
	}

	private gotoPage(pageNumber:number, jump = false) {
		if(pageNumber < 1 || pageNumber > this.pageCount){
			throw new Error('Page out of range');
		}
		
		if (this.isMultiPage) {
			this.scrollToPage(pageNumber, jump);
		}
		else {
			this.currentPage = pageNumber;
			this.loadPage(pageNumber);			
		}
	}

	private adjustPageSize(debounce:boolean = true) {
		this.$timeout.cancel(this.setResizePromise);
		if(debounce){
			return this.setResizePromise = this.$timeout(() => {
				this.adjustPageSize(false);
			}, DEBOUNCE_DELAY);			
		}

		let viewerWindow = this.$window.document.getElementById('sb-dvwindow');
		let relativeScrollbarPosition = viewerWindow.scrollTop / viewerWindow.scrollHeight;

		this.contentWidth = this.calculateContentWidth(this.pageWidth, this.pageScale, this.pageMargin, this.zoomLevel);
		this.contentDiv.css('width',`${this.contentWidth - (this.pageMargin * 2)}px`);

		this.getPageElements().forEach(p => {
			const meta = this.pageMetadata.get(p);
			if(!meta){
				throw new Error(`Could not get metadata for page`)
			}
			
			const newHeight = this.scalePageToWidth(meta.naturalHeight, meta.naturalWidth, (this.contentWidth - (this.pageMargin * 2)));
			angular.element(p).css('height', `${newHeight}px`);
		});

		this.centerDocument();

		// retain scrollbar position by assigning a new scroll value relative to new document height
		viewerWindow.scrollTop = viewerWindow.scrollHeight * relativeScrollbarPosition;

		if(this.docFormat === DocViewerFormat.IMG){
			// mark each loaded page as not loaded if the next width is greater than current
			this.utils
				.dictToArray(this.pageStatuses)
				.filter(page => page.loaded && angular.isDefined(page.nextWidth) && page.nextWidth > page.width)
				.forEach(page => {
					page.loading = page.loaded = false;						
				});			
		}

		if (!this.isSlideView && this.pageCount > 1) {
			this.evaluateView();
		}
		else {
			this.pageQueue.push(1);
		}

		this.loadQueue();
	}

	private centerDocument() {
		const vframeRect = this.viewport.getBoundingClientRect();

		// for ppt and images, center the graphic vertically
		if (this.isSlideView || this.isPictureView) {
			let ypad = (vframeRect.height - this.$window.document.getElementById('dv-pg1').clientHeight - (this.narrowMargin * 2)) / 2;
			if (ypad > 1) {
				angular.element(this.$window.document.getElementById('dv-pgsep1')).css('height', `${ypad + 15}px`);
			}
			else {
				angular.element(this.$window.document.getElementById('dv-pgsep1')).css('height', '15px');
			}
		}
	}

	public viewerResize(event?:Event) {
		this.$timeout.cancel(this.setResizePromise);
		this.setResizePromise = this.$timeout(() => {
			if (!this.userChangedZoom) {
				this.setPageScale();
				this.adjustPageSize(false);
			}
			else {
				this.centerDocument();
			}
		}, DEBOUNCE_DELAY);
	}

	private getScrollbarWidth() {
		let docEl = this.$window.document;
		let scrollDiv = docEl.createElement('div');
		scrollDiv.className = 'sb-dvwindow__sbmeasure';
		docEl.body.appendChild(scrollDiv);
		let sbWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
		docEl.body.removeChild(scrollDiv);
		return sbWidth;
	}

	/**
	 * @param pageNum 1-based number for the document page element
	 * @param height Height in pixels
	 */
	private createPageElem(pageNum:number, height?:number){
		const element = angular
			.element(`<div></div>`)
			.addClass(PAGE_CLASS)
			.attr('id', `dv-pg${pageNum}`);

		if(angular.isNumber(height)){
			element.css('height', `${height}px`)
		}

		return element[0];
	}

	private createSeparatorElem(pageNum?:number){
		const element  = angular
			.element('<div></div>')
			.addClass(SEPARATOR_CLASS);
		
		if(angular.isNumber(pageNum) && pageNum > 0){
			element.attr('id', `dv-pgsep${pageNum}`);
		}

		return element[0];
	}

	/**
	 * Client and Controls API
	 */
	public goToNextPage() {
		if (this.currentPage < this.pageCount) {
			this.gotoPage(this.currentPage + 1);
		}
	}
	public goToPreviousPage() {
		if (this.currentPage > 1) {
			this.gotoPage(this.currentPage -1);
		}
	}
	public goToFirstPage() {
		if (this.currentPage !== 1) {
			this.gotoPage(1, true);
		}
	}

	public goToLastPage() {
		if (this.currentPage !== this.pageCount) {
			this.gotoPage(this.pageCount, true);
		}
	}

	public jumpToPage() {
		// blur page input field after entering a page number
		this.inputDisabled = true;
		this.$timeout(() => this.inputDisabled = false, 0);
		
		let input = +this.inputPage;
		if (!isNaN(input) && input > 0 && input <= this.pageCount) {
			this.gotoPage(input, true);
			this.inputPage = input.toString();
		}
		else {
			this.inputPage = this.currentPage.toString();
		}
	}

	public setCurrentTab(tabIndex) {
		if (this.isTabView) {
			this.loadTab(tabIndex);
		}
	}

	/**
	 * Covnenience method for zooming in by a fixed interval.
	 */
	public zoomIn() {
		if (this.zoomLevel <= MAX_ZOOM_LEVEL) {
			this.setZoom(this.zoomLevel * ZOOM_INTERVAL);
		}
	}

	/**
	 * Covnenience method for zooming out by a fixed interval.
	 */
	public zoomOut() {
		if (this.zoomLevel >= MIN_ZOOM_LEVEL) {
			this.setZoom(this.zoomLevel / ZOOM_INTERVAL);
		}
	}

	/**
	 * Zoom to an arbitrary percentage.
	 */
	public setZoom(percent:number) {
		this.userChangedZoom = true;
		this.zoomLevel = percent;		
		this.adjustPageSize(true);
		this.utils
			.dictToArray(this.pageStatuses)
			.filter(page => page.loaded)
			.forEach(page => {
				let newWidth = this.getRealContentWidth();
				page.nextWidth = this.clampWidth(newWidth);
			});
	}

	/**
	 * Limits a width value between the smallest requestable 
	 * and the maximum specified by the API (if any).
	 * Values in between will be rounded based on the nearest interval.
	 */
	private clampWidth(width:number){		
		let widthIntervals = Math.ceil(width / PAGE_WIDTH_INTERVAL);
		let roundedWidth = widthIntervals * PAGE_WIDTH_INTERVAL;
		
		if(angular.isDefined(this.pageMaxWidth) && roundedWidth > this.pageMaxWidth) {
			return this.pageMaxWidth;
		}
		else if(roundedWidth < MIN_PAGE_WIDTH){
			return MIN_PAGE_WIDTH;
		}

		return roundedWidth;
	}
	
	public init() {
		this.firstLoad = true;
		this.client = this.config(this);
		this.mode = this.client.mode || DocViewerMode.MULTIPAGE;
		this.docFormat = this.client.docFormat || DocViewerFormat.SVG;
		this.isTabView = this.mode === DocViewerMode.TABBED;
		this.isSlideView = this.mode === DocViewerMode.SLIDE;
		this.isPictureView = this.mode === DocViewerMode.PICTURE;
		this.isMultiPage = this.mode === DocViewerMode.MULTIPAGE || this.isTabView;

		if (this.isTabView) {
			// this will be updated after the first page section is loaded
			// for spreadsheets, the client-reported #pages means the number of tabs
			this.pageCount = 1;

			// spreadsheet performance sucks on mobile devices if the page is very large.  This
			// code is for testing but we may opt to put it in until a solution is developed.
			// if (this.isMobileDevice)
			// this.docFormat = DocViewerFormat.IMG;
		}
		else {
			this.pageCount = this.client.pageCount;
		}

		let pageInput = angular.element(this.$window.document.getElementsByClassName('sb-dvtoolbar__page_selector---input')[0]);
		if(this.pageCount){
			pageInput.css('width', `${this.pageCount.toString().length + 1}ch`);
		}
		

		this.scrollbarWidth = this.getScrollbarWidth();

		if (this.isTabView) {
			this.tabList = [];
			// for spreadsheets, use the getMediaInfo call to return the names
			// of the worksheets (tabs)
			this.client
				.getMediaInfo()
				.then(detail => {
					for (let i = 0; i < detail.sheets.length; ++i) {
						this.tabList.push({ index: i, name: detail.sheets[i], selected: false });
					}
					this.tabList[0].selected = true;
				});
		}


		this.isLoading = true;
		let loadPromise = this.waitForDom()
			.then(() => {
				let requestedWidth = this.clampWidth(this.getRealContentWidth());			
				return this.loadPage(1, requestedWidth);
			})
			.then(() => this.evaluateAndLoad())
			.finally(() => {
				this.isLoading = this.firstLoad = false;
			});

		this.pxProgressService.enqueue(loadPromise);
	}

	private waitForDom(){
		// work around for angular 1.5 lazily compiling transcludes now transcluded template isn't compiled yet so we need to wait
		// https://github.com/angular/angular.js/commit/652b83eb226131d131a44453520a569202aa4aac
		return this.$q((resolve, reject) => {
			let interval = this.$interval(() => {
				let viewport = this.$window.document.getElementById('sb-dvwindow');
				let content = this.$window.document.getElementById('sb-dvwindow__content');
				if (viewport && content) {
					this.$interval.cancel(interval);
					interval = null;
					this.viewport = viewport;
					this.contentDiv = angular.element(content);
					resolve();
				}
			}, 50);			
		})
	}

	public destroy(){
		// free data structures holding references to DOM elements
		this.pageStatuses = this.pageMetadata = null;		
	}
}



// WEBPACK FOOTER //
// ./src/phoenix/components/documentViewer/documentViewer.ts